// Considered: AddTemplate molecule — rejected because it renders a flat schema picker for the legacy
// two-dialog flow; this molecule drives the choose step with use-case/type rails and
// multi-column progressive discovery inside a single dialog instance.
import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'localization';
import SchemaCardAtom from '../../atoms/SchemaCard/SchemaCardAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import SpinnerAtom from '../../atoms/Spinner/SpinnerAtom';
import { SegmentedControlAtom } from '../../atoms/SegmentedControl';
import TemplateCardMolecule from '../TemplateCard/TemplateCardMolecule';
import { checkClientSideApplicability } from '@center-platform/spheres/filters';
import { getCreatableTypes } from '../../../utils/getCreatableTypes';
import { useSchemaType } from '../../../hooks/useSchemaType';
import type { Schema, UseCaseRow } from 'types/schema';
import type { Sphere } from 'types/global';
import type { TemplateApproach } from '../../../types/templateApproach';
import './AddSphereChooseStepMolecule.scss';

const noopSelectTemplate = (_template: TemplateApproach): void => undefined;

export interface AddSphereChooseStepMoleculeProps {
  parentSphere: Sphere;
  tab: 'blank' | 'template';
  onTabChange: (tab: 'blank' | 'template') => void;
  schemas: Schema[];
  schemasLoading: boolean;
  useCases: UseCaseRow[];
  onSelectSchema: (schema: Schema) => void;
  onClose: () => void;
  /** Template approaches for the "From a template" tab. */
  approaches?: TemplateApproach[];
  approachesLoading?: boolean;
  onSelectTemplate?: (template: TemplateApproach) => void;
}

const AddSphereChooseStepMolecule: React.FC<AddSphereChooseStepMoleculeProps> = ({
  parentSphere,
  tab,
  onTabChange,
  schemas,
  schemasLoading,
  useCases,
  onSelectSchema,
  onClose,
  approaches = [],
  approachesLoading = false,
  onSelectTemplate,
}) => {
  const { t } = useTranslation();
  const [intent, setIntent] = useState<string | null>(null);
  const [typeCat, setTypeCat] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const topUseCases = useMemo(
    () => useCases.filter((u) => !u.parent_system_name).slice(0, 6),
    [useCases],
  );

  const typeCats = useMemo(() => {
    const seen: string[] = [];
    schemas.forEach((s) => {
      const cat = (s as any).category;
      if (cat && !seen.includes(cat)) seen.push(cat);
    });
    return seen;
  }, [schemas]);

  const parentType = (parentSphere?.properties?.type as string) ?? '';

  // The eligibility evaluator needs the PARENT's own schema in the pool (for its
  // node_label + assignee_filter matching). `schemas` holds only child types, so
  // resolve and inject the parent schema — without it the evaluator returns [].
  const { schema: parentSchema } = useSchemaType(parentType || null);

  // Eligible, creatable child types for parentSphere — drives type cards and gates templates.
  const creatable = useMemo(() => {
    if (!parentType) return [];
    const pool = parentSchema ? [...schemas, parentSchema] : schemas;
    return getCreatableTypes(pool, schemas, { type: parentType });
  }, [schemas, parentSchema, parentType]);

  const creatableSystemNames = useMemo(
    () => new Set(creatable.map((s) => s.system_name)),
    [creatable],
  );

  const filteredSchemas = useMemo(() => {
    let out = [...creatable];
    if (intent) {
      out = out.filter(
        (s) =>
          s.use_case_tags.includes(intent) ||
          useCases.some((u) => u.parent_system_name === intent && s.use_case_tags.includes(u.system_name)),
      );
    }
    if (typeCat) {
      out = out.filter((s) => (s as any).category === typeCat);
    }
    if (query) {
      const q = query.toLowerCase();
      out = out.filter(
        (s) => s.name.toLowerCase().includes(q) || (s.description ?? '').toLowerCase().includes(q),
      );
    }
    return out;
  }, [creatable, intent, typeCat, query, useCases]);

  /** Build schema lookup for resolving templateFor → display name + glyph. */
  const schemaBySystemName = useMemo(() => {
    const map = new Map<string, Schema>();
    schemas.forEach((s) => map.set(s.system_name, s));
    return map;
  }, [schemas]);

  /** Parent sphere property bag for applicability evaluation. */
  const parentProps = useMemo(
    () => (parentSphere?.properties as Record<string, unknown>) ?? {},
    [parentSphere],
  );

  // Merged inventory an approach's `{ aspect, property }` applicability
  // reference (EAINT-899) resolves against — the same shape
  // `getRequestSchemaProperties` produces server-side. Without it,
  // `checkClientSideApplicability` falls back to resolving a bare-aspect
  // reference (`property: ''`) to nothing and fails closed even when the
  // aspect's primary property would otherwise apply.
  const schemaProperties = useMemo(
    () => schemas.flatMap((s) => s.properties ?? []),
    [schemas],
  );

  const filteredApproaches = useMemo(() => {
    let out = approaches;

    // Intent filter: approach must include the selected intent (or a child use-case of it)
    if (intent) {
      out = out.filter(
        (a) =>
          a.use_case_tags.includes(intent) ||
          useCases.some(
            (u) => u.parent_system_name === intent && a.use_case_tags.includes(u.system_name),
          ),
      );
    }

    // Type category filter (matches templateFor's schema category)
    if (typeCat) {
      out = out.filter((a) => {
        if (!a.templateFor) return false;
        const schema = schemaBySystemName.get(a.templateFor);
        return schema && (schema as any).category === typeCat;
      });
    }

    // Search filter
    if (query) {
      const q = query.toLowerCase();
      out = out.filter(
        (a) => a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q),
      );
    }

    // Applicability filter — silent, client-optimistic.
    // Uses the canonical evaluator from @center-platform/spheres so operator coverage
    // and filterGroup recursion stay in sync with the server-side applicabilityValidator.
    const applicabilityContext = {
      ...parentProps,
      ...(parentSphere?.id !== undefined ? { id: parentSphere.id } : {}),
    };
    out = out.filter((a) => checkClientSideApplicability(a.applicability, applicabilityContext, schemaProperties));

    // Type-eligibility gate: template's target type must be creatable under parentSphere.
    // Complements the approach-applicability gate above — both must hold.
    out = out.filter((a) => a.templateFor != null && creatableSystemNames.has(a.templateFor));

    return out;
  }, [approaches, intent, typeCat, query, useCases, schemaBySystemName, parentProps, parentSphere, creatableSystemNames, schemaProperties]);

  const handleIntentClick = useCallback(
    (id: string | null) => {
      setIntent((prev) => (prev === id ? null : id));
    },
    [],
  );

  const handleTypeCatClick = useCallback(
    (cat: string | null) => {
      setTypeCat((prev) => (prev === cat ? null : cat));
    },
    [],
  );

  const tabOptions = [
    { value: 'blank' as const, label: t('addSphere.dialog.tabs.blank') },
    { value: 'template' as const, label: t('addSphere.dialog.tabs.template') },
  ];

  return (
    <div className="add-sphere-choose-step">
      <div className="add-sphere-choose-step__search-bar">
        <div className="add-sphere-choose-step__search-input-wrap">
          <i className="pi pi-search add-sphere-choose-step__search-icon" />
          <input
            className="add-sphere-choose-step__search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              tab === 'blank'
                ? t('addSphere.dialog.search.placeholderBlank')
                : t('addSphere.dialog.search.placeholderTemplate')
            }
            aria-label={t('addSphere.dialog.search.placeholderBlank')}
          />
        </div>
        <SegmentedControlAtom
          options={tabOptions}
          value={tab}
          onChange={(v) => onTabChange(v as 'blank' | 'template')}
          mode="single"
          variant="strip"
          className="add-sphere-choose-step__tabs"
        />
      </div>

      <div className="add-sphere-choose-step__body">
        <aside className="add-sphere-choose-step__rail">
          <div className="add-sphere-choose-step__rail-section-label">
            {t('addSphere.dialog.rail.browseByIntent')}
          </div>
          <button
            className={`add-sphere-choose-step__rail-btn${intent === null ? ' add-sphere-choose-step__rail-btn--active' : ''}`}
            onClick={() => handleIntentClick(null)}
          >
            <i className="pi pi-th-large" />
            <span>{t('addSphere.dialog.rail.all')}</span>
          </button>
          {topUseCases.map((uc) => (
            <button
              key={uc.system_name}
              className={`add-sphere-choose-step__rail-btn${intent === uc.system_name ? ' add-sphere-choose-step__rail-btn--active' : ''}`}
              onClick={() => handleIntentClick(uc.system_name)}
            >
              <i className={`pi ${uc.icon}`} />
              <span>{uc.name}</span>
            </button>
          ))}

          <div className="add-sphere-choose-step__rail-divider" />

          <div className="add-sphere-choose-step__rail-section-label">
            {t('addSphere.dialog.rail.browseByType')}
          </div>
          <button
            className={`add-sphere-choose-step__rail-btn${typeCat === null ? ' add-sphere-choose-step__rail-btn--active' : ''}`}
            onClick={() => handleTypeCatClick(null)}
          >
            <i className="pi pi-shapes" />
            <span>{t('addSphere.dialog.rail.allTypes')}</span>
          </button>
          {typeCats.map((cat) => (
            <button
              key={cat}
              className={`add-sphere-choose-step__rail-btn${typeCat === cat ? ' add-sphere-choose-step__rail-btn--active' : ''}`}
              onClick={() => handleTypeCatClick(cat)}
            >
              <i className="pi pi-box" />
              <span>{cat}</span>
            </button>
          ))}
        </aside>

        <div className="add-sphere-choose-step__main">
          {tab === 'blank' ? (
            <>
              {!query && !intent && (
                <div className="add-sphere-choose-step__recent">
                  <div className="add-sphere-choose-step__section-label">
                    {t('addSphere.dialog.recent')}
                  </div>
                </div>
              )}

              <div className="add-sphere-choose-step__section-label">
                {t('addSphere.dialog.rail.allTypes')}
              </div>

              {schemasLoading ? (
                <div className="add-sphere-choose-step__loading">
                  <SpinnerAtom />
                </div>
              ) : filteredSchemas.length === 0 ? (
                <EmptyStateAtom
                  title={t('addSphere.dialog.empty.noTypes')}
                  icon="pi-inbox"
                />
              ) : (
                <div className="add-sphere-choose-step__grid" data-testid="schema-type-grid">
                  {filteredSchemas.map((s) => (
                    <SchemaCardAtom
                      key={s.system_name}
                      icon={s.glyph ?? null}
                      label={s.name}
                      description={s.description ?? null}
                      onClick={() => onSelectSchema(s)}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {approachesLoading ? (
                <div className="add-sphere-choose-step__loading">
                  <SpinnerAtom />
                </div>
              ) : filteredApproaches.length === 0 ? (
                <EmptyStateAtom
                  title={t('addSphere.dialog.empty.noTemplates')}
                  icon="pi-layout"
                />
              ) : (
                <div
                  className="add-sphere-choose-step__grid add-sphere-choose-step__grid--templates"
                  data-testid="template-grid"
                >
                  {filteredApproaches.map((a) => {
                    const schema = a.templateFor ? schemaBySystemName.get(a.templateFor) : undefined;
                    return (
                      <TemplateCardMolecule
                        key={a.system_name}
                        template={a}
                        schemaName={schema?.name}
                        schemaGlyph={schema?.glyph}
                        onClick={onSelectTemplate ?? noopSelectTemplate}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddSphereChooseStepMolecule;
